import React, { useState, useEffect } from "react";
import { Form, Input, Button, message, Card, Typography, Steps, Space } from "antd";
import { Link } from "react-router-dom";
import {
    ArrowLeftOutlined,
    MailOutlined,
    LockOutlined,
    CheckCircleOutlined,
} from "@ant-design/icons";
import logo from "../assets/images/logo.png";

const { Title, Text } = Typography;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ForgotPassword() {
    const [step, setStep] = useState(0);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    const [emailForm] = Form.useForm();
    const [otpForm] = Form.useForm();
    const [passwordForm] = Form.useForm();

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const isMobile = windowWidth < 576;

    // ===== SEND OTP =====
    const handleSendOTP = async (values) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("email", values.email);

            const res = await fetch(`${API_BASE_URL}/api/admin/forgot-password/send-otp`, {
                method: "POST",
                body: formData,
            });
            const data = await res.json();

            if (!res.ok) throw new Error(data.detail || "Failed to send OTP");

            setEmail(values.email);
            message.success("OTP sent to your email!");
            setStep(1);
            otpForm.resetFields();
        } catch (err) {
            message.error(err.message || "Failed to send OTP");
        } finally {
            setLoading(false);
        }
    };

    // ===== VERIFY OTP =====
    const handleVerifyOTP = async (values) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("email", email);
            formData.append("otp", values.otp);

            const res = await fetch(`${API_BASE_URL}/api/admin/forgot-password/verify-otp`, {
                method: "POST",
                body: formData,
            });
            const data = await res.json();

            if (!res.ok) throw new Error(data.detail || "OTP verification failed");

            message.success("OTP verified!");
            setStep(2);
            passwordForm.resetFields();
        } catch (err) {
            message.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    // ===== RESET PASSWORD =====
    const handleResetPassword = async (values) => {
        if (values.new_password !== values.confirm_password) {
            message.error("Passwords do not match");
            return;
        }
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("email", email);
            formData.append("new_password", values.new_password);

            const res = await fetch(`${API_BASE_URL}/api/admin/forgot-password/reset`, {
                method: "POST",
                body: formData,
            });
            const data = await res.json();

            if (!res.ok) throw new Error(data.detail || "Password reset failed");

            message.success("Password reset successfully!");
            setTimeout(() => {
                window.location.href = "/login";
            }, 2000);
        } catch (err) {
            message.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh",
                background: "linear-gradient(135deg, #007cf0 0%, #7928ca 100%)",
                position: "relative",
                padding: "16px",
            }}
        >
            {/* Subtle dot pattern overlay */}
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.05,
                    backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)",
                    backgroundSize: "20px 20px",
                    pointerEvents: "none",
                }}
            />

            <Card
                style={{
                    width: isMobile ? "100%" : 480,
                    maxWidth: 500,
                    borderRadius: 24,
                    background: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(20px)",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)",
                    border: "1px solid rgba(255,255,255,0.5)",
                    padding: isMobile ? "20px 16px" : "32px 28px",
                    animation: "fadeInUp 0.6s ease",
                }}
                bodyStyle={{ padding: 0 }}
            >
                {/* Back to Login */}
                <div style={{ marginBottom: 16 }}>
                    <Link
                        to="/login"
                        style={{
                            color: "#1f3c88",
                            fontWeight: 500,
                            fontSize: 14,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                        }}
                    >
                        <ArrowLeftOutlined /> Back to Login
                    </Link>
                </div>

                <img
                    src={logo}
                    alt="Logo"
                    style={{
                        width: isMobile ? 80 : 100,
                        margin: "0 auto 16px",
                        display: "block",
                    }}
                />

                <Title
                    level={isMobile ? 3 : 2}
                    style={{
                        textAlign: "center",
                        marginBottom: 20,
                        fontWeight: 700,
                        color: "#1f1f1f",
                    }}
                >
                    IAM Password Reset
                </Title>

                <Steps
                    current={step}
                    size={isMobile ? "small" : "default"}
                    items={[
                        { title: "Email", icon: <MailOutlined /> },
                        { title: "Verify OTP", icon: <CheckCircleOutlined /> },
                        { title: "New Password", icon: <LockOutlined /> },
                    ]}
                    style={{ marginBottom: 24 }}
                />

                {/* EMAIL STEP */}
                {step === 0 && (
                    <Form layout="vertical" form={emailForm} onFinish={handleSendOTP}>
                        <Text style={{ fontSize: 14, color: "#555" }}>
                            Enter your admin email address
                        </Text>
                        <Form.Item
                            name="email"
                            label={<span style={{ fontWeight: 600 }}>Email</span>}
                            rules={[
                                { required: true, message: "Please enter your email" },
                                { type: "email", message: "Invalid email" },
                            ]}
                            style={{ marginTop: 16 }}
                        >
                            <Input
                                placeholder="admin@example.com"
                                style={{ borderRadius: 12, padding: "10px 14px" }}
                            />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 0 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                block
                                size="large"
                                style={{
                                    borderRadius: 12,
                                    height: 48,
                                    fontWeight: 600,
                                    background: "linear-gradient(135deg, #007cf0, #7928ca)",
                                    border: "none",
                                    boxShadow: "0 4px 14px rgba(0,124,240,0.3)",
                                }}
                            >
                                Send OTP
                            </Button>
                        </Form.Item>
                    </Form>
                )}

                {/* OTP STEP */}
                {step === 1 && (
                    <Form layout="vertical" form={otpForm} onFinish={handleVerifyOTP}>
                        <Text style={{ fontSize: 14, color: "#555" }}>
                            Enter the OTP sent to <strong>{email}</strong>
                        </Text>
                        <Form.Item
                            name="otp"
                            label={<span style={{ fontWeight: 600 }}>OTP</span>}
                            rules={[
                                { required: true, message: "Enter OTP" },
                                { len: 6, message: "OTP must be 6 digits" },
                            ]}
                            style={{ marginTop: 16 }}
                        >
                            <Input
                                maxLength={6}
                                placeholder="000000"
                                style={{ borderRadius: 12, padding: "10px 14px" }}
                            />
                        </Form.Item>

                        <Space direction="vertical" style={{ width: "100%" }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                block
                                size="large"
                                style={{
                                    borderRadius: 12,
                                    height: 48,
                                    fontWeight: 600,
                                    background: "linear-gradient(135deg, #007cf0, #7928ca)",
                                    border: "none",
                                    boxShadow: "0 4px 14px rgba(0,124,240,0.3)",
                                }}
                            >
                                Verify OTP
                            </Button>
                            <Button
                                type="link"
                                onClick={() => {
                                    emailForm.resetFields();
                                    setStep(0);
                                }}
                                style={{ fontWeight: 500 }}
                            >
                                Back to Email
                            </Button>
                        </Space>
                    </Form>
                )}

                {/* RESET PASSWORD STEP */}
                {step === 2 && (
                    <Form layout="vertical" form={passwordForm} onFinish={handleResetPassword}>
                        <Text style={{ fontSize: 14, color: "#555" }}>Enter new password</Text>
                        <Form.Item
                            name="new_password"
                            label={<span style={{ fontWeight: 600 }}>New Password</span>}
                            rules={[
                                { required: true, message: "Please enter a new password" },
                                { min: 6, message: "Minimum 6 characters" },
                            ]}
                            style={{ marginTop: 16 }}
                        >
                            <Input.Password
                                style={{ borderRadius: 12, padding: "10px 14px" }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="confirm_password"
                            label={<span style={{ fontWeight: 600 }}>Confirm Password</span>}
                            rules={[
                                { required: true, message: "Please confirm your password" },
                                { min: 6, message: "Minimum 6 characters" },
                            ]}
                        >
                            <Input.Password
                                style={{ borderRadius: 12, padding: "10px 14px" }}
                            />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 0 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                block
                                size="large"
                                style={{
                                    borderRadius: 12,
                                    height: 48,
                                    fontWeight: 600,
                                    background: "linear-gradient(135deg, #007cf0, #7928ca)",
                                    border: "none",
                                    boxShadow: "0 4px 14px rgba(0,124,240,0.3)",
                                }}
                            >
                                Reset Password
                            </Button>
                        </Form.Item>
                    </Form>
                )}
            </Card>

            <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
        </div>
    );
}

export default ForgotPassword;